import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { StepContext } from '../../../creator/step.context';
import { FORM_FIELDS, FORM_STEPS } from '../../../../../constants';
import { useLoggedIn } from '../../../../../store/reducers/user';
import { PURPOSE_SOURCE_VALUES } from '../../../../../constants/sources';
import { getCurrentField } from '../helpers';
import { SheetContent, SheetFooter, SheetHeader } from '../../components/form/components/focused.sheet';
import {
    PhoneFieldHeader,
    SmSFieldHeader,
    PhoneFieldFooter,
    SmsCodeFieldFooter,
    SmsCodeFieldForSheet,
} from './components/mobile';
import {
    AmountField,
    AutoBrandSuggestField,
    AutoModelSuggestField,
    AutoYearSuggestField,
    InitialFeeField,
    PhoneField,
    PurposeField,
    RegionField,
} from './fields';
import { useAutoFieldsManager } from './helpers';

const CustomHeaders = {
    [FORM_FIELDS.PHONE]: PhoneFieldHeader,
    [FORM_FIELDS.SMS_CODE]: SmSFieldHeader,
};

export const ParamsStepSheetView: FC = React.memo(() => {
    const loggedIn = useLoggedIn();
    const { focusedField, setFocusedField, currentStepConfig } = useContext(StepContext);
    const form = useFormContext();
    const { autoModelIsNeeded, autoYearIsNeeded } = useAutoFieldsManager();

    const {
        watch,
        formState: { dirtyFields: _ },
    } = form;

    useEffect(() => {
        if (currentStepConfig.stepName === FORM_STEPS.PARAMS) {
            if (!loggedIn) {
                setFocusedField(FORM_FIELDS.PHONE);
            }
        }
    }, []);

    const [selectedPurpose] = watch([FORM_FIELDS.PURPOSE]);

    const isAutoCreditSelected = useMemo(
        () => [PURPOSE_SOURCE_VALUES.NEW_CAR, PURPOSE_SOURCE_VALUES.USED_CAR].includes(selectedPurpose),
        [selectedPurpose],
    );

    const fieldsList = useMemo(() => {
        const autoFields = isAutoCreditSelected
            ? [
                  { name: FORM_FIELDS.INITIAL_FEE, view: InitialFeeField },
                  { name: FORM_FIELDS.AUTO_BRAND, view: AutoBrandSuggestField },
                  ...(autoModelIsNeeded ? [{ name: FORM_FIELDS.AUTO_MODEL, view: AutoModelSuggestField }] : []),
                  ...(autoYearIsNeeded ? [{ name: FORM_FIELDS.AUTO_YEAR, view: AutoYearSuggestField }] : []),
              ]
            : [];

        const notLoggedInFields = !loggedIn
            ? [
                  { name: FORM_FIELDS.PHONE, view: PhoneField },
                  { name: FORM_FIELDS.SMS_CODE, view: SmsCodeFieldForSheet },
              ]
            : [];

        return [
            ...notLoggedInFields,
            { name: FORM_FIELDS.PURPOSE, view: PurposeField },
            ...autoFields,
            { name: FORM_FIELDS.AMOUNT, view: AmountField },
            { name: FORM_FIELDS.REGION, view: RegionField },
        ];
    }, [loggedIn, isAutoCreditSelected, autoModelIsNeeded, autoYearIsNeeded]);

    const { field: currentField } = useMemo(
        () => getCurrentField(fieldsList, focusedField),
        [fieldsList, focusedField],
    );

    const CustomHeader = currentField && CustomHeaders[currentField.name];

    const CustomFooter = useMemo(() => {
        if (!currentField) {
            return undefined;
        }
        switch (currentField.name) {
            case FORM_FIELDS.PHONE: {
                return <PhoneFieldFooter />;
            }
            case FORM_FIELDS.SMS_CODE: {
                return <SmsCodeFieldFooter />;
            }
            default: {
                return undefined;
            }
        }
    }, [currentField]);

    const hideCloseButton = useMemo(
        () =>
            Boolean(
                currentField &&
                    !loggedIn &&
                    ([FORM_FIELDS.PHONE, FORM_FIELDS.SMS_CODE] as Array<string>).includes(currentField.name),
            ),
        [currentField, loggedIn],
    );

    return (
        <>
            <SheetHeader customTitle={CustomHeader} hideCloseBtn={hideCloseButton} />
            <SheetContent fieldsList={fieldsList} />
            <SheetFooter fieldsList={fieldsList} customFooter={CustomFooter} />
        </>
    );
});

ParamsStepSheetView.displayName = 'ParamsStepSheetView';
